import { z } from 'zod'

import { settingMutationErrorMessage } from '@/modules/hierarchy'

import type { PrinterCounterKind } from '../../api/assets-api'

export type FormatOption = { id: string; label: string }

export type CatalogState = 'ready' | 'loading' | 'retrying' | 'error'

export const PRINTER_RESOURCE_TYPE = 'printer'
export const PAGE_COUNT_FORMAT_KEY = 'print.page-count.format-preference'
export const DEFAULT_FORMAT_ORDER = ['life', 'equivalent', 'engine'] as const
export const COUNTERS_TO_DISPLAY_KEY = 'print.counters-to-display'
export const DEFAULT_COUNTERS = ['color', 'mono'] as const

const DEFAULT_FORMAT_NAMES: Readonly<Record<string, string>> = {
  life: 'Life',
  equivalent: 'Equivalent',
  engine: 'Engine',
}

const PRE_SCHEMA_5_EQUIVALENT = 'equiv'

const FORMAT_ID_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,63}$/

export function canonicalFormatId(id: string) {
  return id === PRE_SCHEMA_5_EQUIVALENT ? 'equivalent' : id
}

function distinctAfterCanonical(ids: string[]) {
  return new Set(ids.map(canonicalFormatId)).size === ids.length
}

export const formatPrioritySchema = z
  .array(
    z
      .string()
      .regex(
        FORMAT_ID_PATTERN,
        'Page-count format ids must start with a letter and use only letters, digits, and underscores (64 characters at most).',
      ),
  )
  .max(32, 'Choose at most 32 page-count formats.')
  .refine(distinctAfterCanonical, 'Page-count formats must not repeat.')

const storedFormatPriority = z.array(z.string())

export function decodeFormatPriority(value: unknown): string[] {
  const stored = storedFormatPriority.safeParse(value)
  return stored.success ? stored.data : [...DEFAULT_FORMAT_ORDER]
}

export function formatLabel(id: string, catalog: readonly FormatOption[]) {
  const canonical = canonicalFormatId(id)
  return (
    catalog.find((option) => option.id === canonical)?.label ??
    DEFAULT_FORMAT_NAMES[canonical] ??
    asPhrase(canonical)
  )
}

function asPhrase(id: string) {
  const words = id.replaceAll('_', ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export function reportedFormatOptions(
  reportedIds: readonly string[],
  vocabulary: readonly FormatOption[],
): FormatOption[] {
  const reported = new Set(reportedIds.map(canonicalFormatId))
  const vocabularyIds = vocabulary.map((option) => option.id)
  const toOption = (id: string) => ({ id, label: formatLabel(id, vocabulary) })
  return [
    ...vocabularyIds.filter((id) => reported.has(id)).map(toOption),
    ...[...reported]
      .filter((id) => !vocabularyIds.includes(id))
      .map(toOption)
      .sort(byBuiltInOrderThenLabel),
  ]
}

function byBuiltInOrderThenLabel(left: FormatOption, right: FormatOption) {
  return builtInRank(left.id) - builtInRank(right.id) || left.label.localeCompare(right.label)
}

function builtInRank(id: string) {
  const rank = DEFAULT_FORMAT_ORDER.findIndex((builtIn) => builtIn === id)
  return rank === -1 ? DEFAULT_FORMAT_ORDER.length : rank
}

export function formatListLabel(value: unknown, catalog: readonly FormatOption[]) {
  if (!Array.isArray(value)) return String(value)
  return value.map((id) => formatLabel(String(id), catalog)).join(', ')
}

export function printSettingsSaveErrorMessage(error: unknown) {
  return settingMutationErrorMessage(error, 'Couldn’t save the format priority. Try again.', {
    INVALID_SETTING_VALUE: 'The server rejected the format list. Reload the page and try again.',
    RESOURCE_NOT_FOUND: 'This printer has moved or is no longer active. Reload the page.',
  })
}

const storedCounters = z.array(z.string())

export function decodeCountersToDisplay(value: unknown): string[] {
  const stored = storedCounters.safeParse(value)
  return stored.success ? stored.data : [...DEFAULT_COUNTERS]
}

export function counterLabel(kindId: string, catalog: readonly PrinterCounterKind[]) {
  return catalog.find((kind) => kind.kindId === kindId)?.name ?? asPhrase(kindId)
}

export function counterListLabel(value: unknown, catalog: readonly PrinterCounterKind[]) {
  if (!Array.isArray(value)) return String(value)
  return value.map((kindId) => counterLabel(String(kindId), catalog)).join(', ')
}

export function countersSaveErrorMessage(error: unknown) {
  return settingMutationErrorMessage(error, 'Couldn’t save the counters to display. Try again.', {
    INVALID_SETTING_VALUE: 'The server rejected the counter list. Reload the page and try again.',
  })
}
